// Texture Forge model (FRG-1/2, ARCH §21). A *pure*, deterministic procedural
// painter over the same Canvas-2D texture family the atlas uses (D7): given a
// small parameter struct it paints one 16×16 RGBA tile, byte-for-byte identically
// for identical params. "Forging" serializes the params to a compact base64 seed
// string (never a bitmap); importing pastes the string back. The only DOM-touching
// function is `blitForgeTile`, which stamps a painted buffer onto the shared atlas
// canvas — everything else is hermetic and unit-testable without a DOM.
import { TILE_PX } from '../../../sim/mesh/atlasmap';
export { TILE_PX } from '../../../sim/mesh/atlasmap';

/** Current seed-string struct version. A newer version is rejected (E-33). */
export const FORGE_VERSION = 1;
/** Serialized struct size in bytes (base64 → ~28 chars). See `packSeed`. */
export const FRG_SEED_BYTES = 21;
/** Palette length bounds (basePalette[2..4], FRG-1). */
export const FORGE_BASE_MIN = 2;
export const FORGE_BASE_MAX = 4;
/** Octave + feature-scale bounds (FRG-1). */
export const FORGE_OCTAVES_MIN = 1;
export const FORGE_OCTAVES_MAX = 4;
export const FORGE_SCALE_MIN = 1;
export const FORGE_SCALE_MAX = 16;

export type ForgeNoise = 'value' | 'simplex' | 'checker';
const NOISE_KINDS: readonly ForgeNoise[] = ['value', 'simplex', 'checker'];

export type RGB = readonly [number, number, number];

/** A forged-skin parameter set (the whole model). */
export interface ForgeParams {
  /** 2..4 base colours, blended across the noise field. */
  readonly base: readonly RGB[];
  /** A single accent colour scattered as dither flecks. */
  readonly accent: RGB;
  readonly noise: ForgeNoise;
  /** Octaves of fractal noise, 1..4. */
  readonly octaves: number;
  /** Feature size in pixels, 1..16 (larger = smoother). */
  readonly scale: number;
  /** Accent fleck probability, 0..1. */
  readonly dither: number;
  /** Bevel/edge-darkening strength, 0..1. */
  readonly edgeShade: number;
}

/** A neutral slate default so an un-forged `forged_block` still reads as a skin. */
export const DEFAULT_FORGE_PARAMS: ForgeParams = {
  base: [
    [96, 100, 108],
    [140, 146, 156],
  ],
  accent: [70, 130, 180],
  noise: 'value',
  octaves: 3,
  scale: 4,
  dither: 0.12,
  edgeShade: 0.35,
};

/** Raised by `unpackSeed` when the struct version is unknown (E-33). */
export class ForgeVersionError extends Error {
  constructor(readonly found: number) {
    super(`unsupported forge seed version ${found} (expected ${FORGE_VERSION})`);
    this.name = 'ForgeVersionError';
  }
}

const clampByte = (v: number): number => Math.max(0, Math.min(255, Math.round(v)));
const clampRgb = (c: RGB): RGB => [clampByte(c[0]), clampByte(c[1]), clampByte(c[2])];
const clamp01 = (v: number): number => (Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 0);
const clampInt = (v: number, lo: number, hi: number, dflt: number): number =>
  Number.isFinite(v) ? Math.max(lo, Math.min(hi, Math.round(v))) : dflt;

/** Coerce arbitrary/UI input into a well-formed, in-range parameter set. */
export function clampForgeParams(p: ForgeParams): ForgeParams {
  const base = p.base.slice(0, FORGE_BASE_MAX).map(clampRgb);
  while (base.length < FORGE_BASE_MIN) base.push(base[base.length - 1] ?? [128, 128, 128]);
  return {
    base,
    accent: clampRgb(p.accent),
    noise: NOISE_KINDS.includes(p.noise) ? p.noise : 'value',
    octaves: clampInt(p.octaves, FORGE_OCTAVES_MIN, FORGE_OCTAVES_MAX, 1),
    scale: clampInt(p.scale, FORGE_SCALE_MIN, FORGE_SCALE_MAX, 4),
    dither: clamp01(p.dither),
    edgeShade: clamp01(p.edgeShade),
  };
}

// ── Seed string (base64 of a fixed 21-byte struct) ──────────────────────────
// [0]=version [1]=(noise|baseLen<<2) [2]=octaves [3]=scale [4]=dither*255
// [5]=edgeShade*255 [6..17]=4×RGB base (unused slots zeroed) [18..20]=accent RGB.

function packBytes(p: ForgeParams): Uint8Array {
  const c = clampForgeParams(p);
  const b = new Uint8Array(FRG_SEED_BYTES);
  b[0] = FORGE_VERSION;
  b[1] = (NOISE_KINDS.indexOf(c.noise) & 0x03) | ((c.base.length & 0x07) << 2);
  b[2] = c.octaves;
  b[3] = c.scale;
  b[4] = clampByte(c.dither * 255);
  b[5] = clampByte(c.edgeShade * 255);
  for (let i = 0; i < FORGE_BASE_MAX; i++) {
    const col = c.base[i] ?? [0, 0, 0];
    b[6 + i * 3] = col[0]; b[7 + i * 3] = col[1]; b[8 + i * 3] = col[2];
  }
  b[18] = c.accent[0]; b[19] = c.accent[1]; b[20] = c.accent[2];
  return b;
}

function bytesToBase64(b: Uint8Array): string {
  let s = '';
  for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]!);
  return btoa(s);
}

function base64ToBytes(s: string): Uint8Array {
  const bin = atob(s);
  const b = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) b[i] = bin.charCodeAt(i) & 0xff;
  return b;
}

/** Serialize params to a shareable seed string (base64 of the struct). */
export function packSeed(p: ForgeParams): string {
  return bytesToBase64(packBytes(p));
}

/** Decode a well-formed struct's bytes into params (the canonical, byte-quantized
 *  form). Shared by `unpackSeed` and the painter so a tile is a pure function of
 *  the seed string, not of sub-quantization precision in the caller's input. */
function decodeParams(bytes: Uint8Array): ForgeParams {
  const noise = NOISE_KINDS[bytes[1]! & 0x03] ?? 'value';
  const baseLen = Math.max(FORGE_BASE_MIN, Math.min(FORGE_BASE_MAX, (bytes[1]! >> 2) & 0x07));
  const base: RGB[] = [];
  for (let i = 0; i < baseLen; i++) base.push([bytes[6 + i * 3]!, bytes[7 + i * 3]!, bytes[8 + i * 3]!]);
  return clampForgeParams({
    base,
    accent: [bytes[18]!, bytes[19]!, bytes[20]!],
    noise,
    octaves: bytes[2]!,
    scale: bytes[3]!,
    dither: bytes[4]! / 255,
    edgeShade: bytes[5]! / 255,
  });
}

/**
 * Parse a seed string back to params. Rejects a malformed or newer-version
 * struct with a thrown error (never crashes the caller, E-33). Callers in the UI
 * catch and surface the message.
 */
export function unpackSeed(seed: string): ForgeParams {
  let bytes: Uint8Array;
  try {
    bytes = base64ToBytes(seed.trim());
  } catch {
    throw new Error('forge seed is not valid base64');
  }
  if (bytes.length !== FRG_SEED_BYTES) throw new Error(`forge seed must be ${FRG_SEED_BYTES} bytes, got ${bytes.length}`);
  const version = bytes[0]!;
  if (version !== FORGE_VERSION) throw new ForgeVersionError(version);
  return decodeParams(bytes);
}

// ── Deterministic painter ───────────────────────────────────────────────────

/** Small deterministic PRNG (legacy mulberry32, as the atlas uses). */
function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Fold the packed struct to a 32-bit seed so identical params → identical noise. */
function seedOf(bytes: Uint8Array): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < bytes.length; i++) { h ^= bytes[i]!; h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}

/** Hashed lattice value in [0,1] for integer (ix,iz) with a seed. */
function latticeValue(ix: number, iz: number, seed: number): number {
  let h = seed ^ Math.imul(ix | 0, 0x27d4eb2d) ^ Math.imul(iz | 0, 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
  return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
}

const smooth = (t: number): number => t * t * (3 - 2 * t);
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Bilinearly-interpolated value noise sampled at (x,z) with cell size `cell`. */
function valueNoise(x: number, z: number, cell: number, seed: number): number {
  const fx = x / cell, fz = z / cell;
  const x0 = Math.floor(fx), z0 = Math.floor(fz);
  const tx = smooth(fx - x0), tz = smooth(fz - z0);
  const v00 = latticeValue(x0, z0, seed), v10 = latticeValue(x0 + 1, z0, seed);
  const v01 = latticeValue(x0, z0 + 1, seed), v11 = latticeValue(x0 + 1, z0 + 1, seed);
  return lerp(lerp(v00, v10, tx), lerp(v01, v11, tx), tz);
}

/** Fractal field in [0,1] combining `octaves` of the chosen noise at `scale`. */
function noiseField(kind: ForgeNoise, x: number, z: number, scale: number, octaves: number, seed: number): number {
  if (kind === 'checker') {
    // A hard N×N checker (scale = cell size); the octaves add a faint sub-grid.
    let v = ((Math.floor(x / scale) + Math.floor(z / scale)) & 1) ? 0.82 : 0.18;
    if (octaves > 1) v += (((Math.floor(x / Math.max(1, scale >> 1)) + Math.floor(z / Math.max(1, scale >> 1))) & 1) ? 0.08 : -0.08);
    return clamp01(v);
  }
  // 'simplex' shares the value-lattice but samples on a sheared grid so the grain
  // reads differently from 'value' (a lightweight, fully-deterministic stand-in).
  const shear = kind === 'simplex' ? 0.5 : 0;
  let amp = 1, freq = 1, sum = 0, norm = 0;
  for (let o = 0; o < octaves; o++) {
    const cell = Math.max(1, scale / freq);
    const sx = x + (kind === 'simplex' ? z * shear : 0);
    sum += amp * valueNoise(sx, z, cell, seed + o * 1013);
    norm += amp;
    amp *= 0.5; freq *= 2;
  }
  return clamp01(sum / norm);
}

/** Blend the base palette by a field value n∈[0,1]. */
function paletteAt(base: readonly RGB[], n: number): RGB {
  if (base.length === 1) return base[0]!;
  const pos = clamp01(n) * (base.length - 1);
  const i = Math.min(base.length - 2, Math.floor(pos));
  const t = pos - i;
  const a = base[i]!, b = base[i + 1]!;
  return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
}

/**
 * Paint one 16×16 tile from params into a fresh RGBA buffer (pure/deterministic).
 * Identical params ⇒ byte-identical output (the FRG golden invariant).
 */
export function paintForgeTile(p: ForgeParams): Uint8ClampedArray {
  // Paint from the canonical byte-quantized params so the tile is a pure function
  // of the seed string (identical seed ⇒ byte-identical tile — the FRG golden).
  const bytes = packBytes(p);
  const c = decodeParams(bytes);
  const seed = seedOf(bytes);
  const rng = mulberry32(seed);
  const n = TILE_PX;
  const out = new Uint8ClampedArray(n * n * 4);
  const edge = c.edgeShade;
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const field = noiseField(c.noise, x, y, c.scale, c.octaves, seed);
      let [r, g, b] = paletteAt(c.base, field);
      // Accent dither: scatter flecks with probability `dither`.
      if (rng() < c.dither) { r = c.accent[0]; g = c.accent[1]; b = c.accent[2]; }
      // Edge bevel: lighten the top/left, darken the bottom/right + border ring.
      if (edge > 0) {
        const border = x === 0 || y === 0 || x === n - 1 || y === n - 1;
        const bevel = ((n - 1 - y) + (n - 1 - x)) / (2 * (n - 1)); // 1 at top-left → 0 at bottom-right
        const shade = 1 + edge * (bevel - 0.5) * 0.8 - (border ? edge * 0.4 : 0);
        r *= shade; g *= shade; b *= shade;
      }
      const i = (y * n + x) * 4;
      out[i] = clampByte(r); out[i + 1] = clampByte(g); out[i + 2] = clampByte(b); out[i + 3] = 255;
    }
  }
  return out;
}

/** A 16×16 `ImageData` of the painted tile (the cast pins the buffer type for the
 *  DOM `ImageData` overload under TS 5.7). DOM-only. */
export function forgeImageData(params: ForgeParams): ImageData {
  return new ImageData(paintForgeTile(params) as Uint8ClampedArray<ArrayBuffer>, TILE_PX, TILE_PX);
}

/**
 * Stamp a painted tile onto the shared atlas canvas at tile index `tile`. The
 * only DOM-touching entry point besides the preview (FRG-2). Callers flag the
 * atlas texture for re-upload afterward.
 */
export function blitForgeTile(canvas: HTMLCanvasElement, tile: number, params: ForgeParams): void {
  const ctx = canvas.getContext('2d')!;
  ctx.putImageData(forgeImageData(params), tile * TILE_PX, 0);
}
